import { difference } from 'lodash';
import { types } from 'mobx-state-tree';
import { useStore } from 'mobx-store-provider';

import { Result, ResultSummary } from '../../../shared-components/Exercise/utils.store';

const UserAnser = types
  .model('UserAnser', {
    questionId: types.identifier,
    answers: types.array(types.string),
  })
  .actions((self) => ({
    setAnswers(answers: string[]) {
      self.answers.replace(answers);
    },
  }));

export const QuestionInfo = types
  .model('QuestionInfo', {
    questionId: types.identifier,
    isSingleAnswer: types.optional(types.boolean, false),
    optionIds: types.array(types.string),
    correctOptionIds: types.array(types.string),
  })
  .actions((self) => ({
    findOptionIndexById(id: string) {
      return self.optionIds.findIndex((option) => option === id);
    },
  }));

export const ExerciseMultipleChoiceDoTestStore = types
  .model('ExerciseMultipleChoiceStoreDoTest')
  .props({
    id: types.identifier,
    userAnswers: types.map(UserAnser),
    questionInfos: types.map(QuestionInfo),
    results: types.map(Result),
    resultSummary: types.maybe(ResultSummary),
    isViewResult: false,
    retryCount: 0,
  })
  .actions((self) => ({
    setQuestionInfos(infos) {
      self.questionInfos.replace(infos);
    },
    setUserAnswers(answers) {
      self.userAnswers.replace(answers);
    },
    answer(questionId: string, optionId: string, isSingleAnswer: boolean) {
      const question = self.userAnswers.get(questionId);
      const questionInfo = self.questionInfos.get(questionId);

      const correctOptions = questionInfo ? questionInfo?.correctOptionIds : [];

      if (!self.userAnswers.has(questionId)) {
        self.userAnswers.put({ questionId, answers: [optionId] });
      } else if (isSingleAnswer) {
        question.setAnswers([optionId]);
      } else {
        const answered = question.answers;
        if (answered.includes(optionId)) {
          question.setAnswers(answered.filter((idx) => idx !== optionId));
        } else {
          question.setAnswers([...answered, optionId]);
          if (question.answers.length > correctOptions.length) {
            question.answers.splice(0, 1);
          }
        }
      }
    },
    viewResult() {
      self.isViewResult = true;
    },
    retry() {
      self.retryCount++;
      self.isViewResult = false;
      self.userAnswers.clear();
    },
    check() {
      const { questionInfos, userAnswers } = self;

      let correct = 0;
      questionInfos.forEach(({ questionId, correctOptionIds }) => {
        const userAnswer = userAnswers.get(questionId);

        if (!userAnswer?.answers.length) {
          self.results.put({ questionId, isMissing: true });
          return;
        }

        if (difference(correctOptionIds, userAnswer.answers).length === 0) {
          self.results.put({ questionId, isCorrect: true });
          correct++;
        } else {
          self.results.put({ questionId, isIncorrect: true });
        }
      });

      self.resultSummary = ResultSummary.create({ correct, total: self.questionInfos.size });
    },
  }))
  .views((self) => ({
    preparePayload() {
      const userAnswers = [];
      self.userAnswers.forEach((answer) => {
        userAnswers.push({ questionId: answer.questionId, answerIds: answer.answers });
      });

      return {
        exerciseId: self.id,
        type: 'MULTIPLE_CHOICE',
        isCompleted: self.isViewResult,
        userAnswers,
      };
    },
  }));

export const useExerciseMultipleChoiceDoTestStore = () =>
  useStore(ExerciseMultipleChoiceDoTestStore);

export const useExerciseQuestionInfoStore = () => useStore(QuestionInfo);
